import { Suspense, lazy, type ComponentType, type ReactNode } from 'react';
import { Outlet, useRouteError, type RouteObject } from 'react-router';
import AppError from '@/app/error';
import Loading from '@/app/loading';
import NotFound from '@/app/not-found';

/**
 * The route table, derived from the file tree.
 *
 * This app used Next's App Router, whose one genuinely useful property here was
 * that a route *is* a file: you add a page by creating it, and there is no
 * second list to fall out of step with the tree. Hand-writing seventy-two
 * routes would have given that up for nothing, so the convention is kept and
 * the table is derived from it:
 *
 *   src/app/dashboard/tools/page.tsx            ->  /dashboard/tools
 *   src/app/dashboard/tools/[id]/edit/page.tsx  ->  /dashboard/tools/:id/edit
 *   src/app/dashboard/page.tsx                  ->  /dashboard          (index)
 *   src/app/dashboard/layout.tsx                ->  wraps everything below it
 *
 * `import.meta.glob` is resolved by Vite at build time, so by the time this
 * ships it is a static list — each page in its own lazily-loaded chunk.
 *
 * The directory keeps the name `app/` so that removing Next did not also move a
 * hundred files in the same diff. It is where routes live; nothing about it is
 * Next any more.
 */
const pageModules = import.meta.glob('/src/app/**/page.tsx');

/*
 * Layouts are eager, and deliberately: they sit *above* the page in the tree, so
 * a lazily-loaded layout is a second waterfall request before the page beneath
 * it can start loading. There are two of them and they are small.
 */
const layoutModules = import.meta.glob('/src/app/**/layout.tsx', { eager: true });

type PageModule = { default: ComponentType };
type LayoutComponent = ComponentType<{ children: ReactNode }>;

/** `/src/app/dashboard/tools/[id]/edit/page.tsx` -> `dashboard/tools/:id/edit` */
function routePathFor(file: string, kind: 'page' | 'layout'): string {
  return file
    .replace('/src/app/', '')
    .replace(new RegExp(`(^|/)${kind}\\.tsx$`), '')
    .replace(/\[([^\]]+)\]/g, ':$1');
}

/** Segment-aware: `dashboard` encloses `dashboard/tools`, but `dash` encloses nothing. */
function encloses(dir: string, path: string): boolean {
  return dir === '' || path === dir || path.startsWith(`${dir}/`);
}

export function buildRoutes(): RouteObject[] {
  const layoutEntries = Object.entries(layoutModules)
    .map(([file, mod]) => ({
      dir: routePathFor(file, 'layout'),
      Layout: (mod as PageModule).default as LayoutComponent,
    }))
    // Shallowest first, so a parent branch always exists before its child.
    .sort((a, b) => a.dir.split('/').length - b.dir.split('/').length);

  const layoutDirs = layoutEntries.map((entry) => entry.dir);

  const root: RouteObject = { path: '/', children: [] };
  const branches = new Map<string, RouteObject>();

  /*
   * Which branch owns this path: the deepest layout that encloses it, or the
   * root when none does (`/login` and `/pending-approval` sit outside the
   * dashboard chrome by design — a pending account must be able to see the
   * waiting room without the sidebar's approved-staff gate bouncing it).
   */
  const branchFor = (path: string): RouteObject =>
    layoutDirs
      .filter((dir) => encloses(dir, path))
      .reduce<RouteObject>((deepest, dir) => branches.get(dir) ?? deepest, root);

  for (const { dir, Layout } of layoutEntries) {
    const branch: RouteObject = {
      /*
       * Absolute, not relative. React Router allows a child path that starts
       * with its parent's, and stating the whole path is what makes this table
       * readable in a debugger — a bare `tools` tells you nothing about where
       * it hangs.
       */
      path: `/${dir}`,
      element: (
        <Layout>
          <Outlet />
        </Layout>
      ),
      children: [],
    };
    // The enclosing layout is the one for this directory's parent, which is ''
    // (i.e. the root) for a top-level layout.
    branchFor(dir.includes('/') ? dir.replace(/\/[^/]+$/, '') : '').children!.push(branch);
    branches.set(dir, branch);
  }

  for (const [file, load] of Object.entries(pageModules)) {
    const path = routePathFor(file, 'page');
    const Page = lazy(load as () => Promise<PageModule>);
    const branch = branchFor(path);

    branch.children!.push(
      // A page sitting directly in a layout's own directory is that layout's
      // index route: `dashboard/page.tsx` is what `/dashboard` renders.
      branch.path === `/${path}`
        ? { index: true, element: <Page /> }
        : { path: `/${path}`, element: <Page /> },
    );
  }

  /*
   * Anything the tree does not claim. Cloudflare already serves index.html for
   * every unmatched path (wrangler.toml: not_found_handling =
   * single-page-application), so an unknown URL reaches this rather than
   * 404ing at the edge.
   */
  root.children!.push({ path: '*', element: <NotFound /> });

  return [
    {
      path: '/',
      element: (
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      ),
      errorElement: <RouteError />,
      children: root.children,
    },
  ];
}

/**
 * Adapts the page Next rendered for a thrown error to React Router's contract.
 *
 * Next handed `error.tsx` a `reset()` that re-rendered the segment. There is no
 * equivalent here — the router has already unmounted the subtree that threw —
 * so the honest "try again" is a reload, which is what a person clicking it
 * means anyway.
 */
function RouteError() {
  const error = useRouteError();
  return (
    <AppError
      error={error instanceof Error ? error : new Error(String(error))}
      reset={() => window.location.reload()}
    />
  );
}
